import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  HTTP_METHOD,
  QUERY_NAME,
  REFETCH_MODE,
  SERVICES,
  WORKER_SETTING_MESSAGES,
} from "@lhd/constants";
import axios from "@lhd/libs/axios";
import {
  CategoryStatusUserSettingItem,
  FetchCategoryStatusUserSettingsParams,
  ResetCategoryStatusUserSettingsPayload,
  SaveCategoryStatusUserSettingsPayload,
} from "@lhd/types";
import { getAccessToken, getCompanyId, getUid } from "@lhd/utility";
import { notification } from "antd";

/**
 * Hook to fetch user & company personalized category/status settings
 */
export const useFetchCategoryStatusUserSettings = (
  params: Partial<FetchCategoryStatusUserSettingsParams>,
  enabledOverride = true,
) => {
  const companyId = params.company_id || getCompanyId();
  const screen = params.screen;
  const recordType = params.record_type;
  const settingSource = params.setting_source;
  const objectType = params.object_type;
  const objectId = params.object_id;
  const includeHidden = params.include_hidden ?? false;
  const uid = getUid();
  const accessToken = getAccessToken();

  return useQuery(
    [
      QUERY_NAME.CATEGORY_STATUS_USER_SETTINGS,
      companyId,
      screen,
      recordType,
      includeHidden,
      uid,
      settingSource,
      objectType,
      objectId,
    ],
    () => {
      const queryParams: Record<string, any> = {
        company_id: companyId,
        screen,
        record_type: recordType,
        per_page: 1000,
      };
      if (settingSource) {
        queryParams.setting_source = settingSource;
      }
      if (objectType) {
        queryParams.object_type = objectType;
      }
      if (objectId !== undefined && objectId !== null) {
        queryParams.object_id = objectId;
      }
      if (includeHidden) {
        queryParams.include_hidden = true;
      }

      return axios({
        method: HTTP_METHOD.GET,
        url: SERVICES.CATEGORY_STATUS_USER_SETTINGS,
        params: queryParams,
      })
        .then((res) => {
          const raw = res.data;
          if (Array.isArray(raw)) return raw as CategoryStatusUserSettingItem[];
          if (Array.isArray(raw?.data))
            return raw.data as CategoryStatusUserSettingItem[];
          return [] as CategoryStatusUserSettingItem[];
        })
        .catch((error) => {
          // If 404 (no settings or records for this screen), return empty array safely
          if (error?.response?.status === 404) {
            return [] as CategoryStatusUserSettingItem[];
          }
          throw error;
        });
    },
    {
      cacheTime: 5 * 60 * 1000,
      staleTime: 0,
      enabled:
        !!companyId &&
        !!screen &&
        !!recordType &&
        !!accessToken &&
        enabledOverride,
      refetchOnMount: REFETCH_MODE.ALWAYS,
      refetchOnReconnect: REFETCH_MODE.ALWAYS,
      refetchOnWindowFocus: false,
    },
  );
};

/**
 * Hook to save user & company personalized category/status settings (PATCH)
 */
export const useSaveCategoryStatusUserSettings = (options?: {
  showSuccessNotification?: boolean;
}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload }: { payload: SaveCategoryStatusUserSettingsPayload }) =>
      axios({
        method: HTTP_METHOD.PATCH,
        url: SERVICES.CATEGORY_STATUS_USER_SETTINGS,
        data: payload,
      }).then((res) => {
        const raw = res.data;
        if (Array.isArray(raw)) return raw as CategoryStatusUserSettingItem[];
        if (Array.isArray(raw?.data))
          return raw.data as CategoryStatusUserSettingItem[];
        return [] as CategoryStatusUserSettingItem[];
      }),
    {
      onSuccess(data, variables) {
        // Update queries in cache directly with returned data without triggering background refetch
        if (data && variables?.payload) {
          const {
            company_id,
            screen,
            record_type,
            setting_source,
            object_type,
            object_id,
          } = variables.payload;
          queryClient.setQueriesData(
            {
              predicate: (query) => {
                const key = query.queryKey;
                const matchesBase =
                  Array.isArray(key) &&
                  key[0] === QUERY_NAME.CATEGORY_STATUS_USER_SETTINGS &&
                  key[1] === company_id &&
                  key[2] === screen &&
                  key[3] === record_type;
                if (!matchesBase) return false;
                const keySettingSource = key[6];
                const keyObjectType = key[7];
                const keyObjectId = key[8];

                if (setting_source && keySettingSource !== setting_source) {
                  return false;
                }
                if (object_type && keyObjectType !== object_type) {
                  return false;
                }
                if (object_id !== undefined && object_id !== null) {
                  return String(keyObjectId) === String(object_id);
                }
                return keyObjectId === undefined || keyObjectId === null;
              },
            },
            (oldData: any) => {
              if (!Array.isArray(data)) return oldData;
              return data;
            },
          );
        }

        if (options?.showSuccessNotification) {
          notification.success({
            message: WORKER_SETTING_MESSAGES.SAVE_SUCCESS,
          });
        }
      },
    },
  );
};

/**
 * Hook to reset user & company personalized category/status settings (DELETE)
 * If category_status_company_id is provided, resets single item.
 * If omitted, resets entire screen + record_type group.
 */
export const useResetCategoryStatusUserSettings = (options?: {
  showSuccessNotification?: boolean;
}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload }: { payload: ResetCategoryStatusUserSettingsPayload }) =>
      axios({
        method: HTTP_METHOD.DELETE,
        url: SERVICES.CATEGORY_STATUS_USER_SETTINGS,
        data: payload,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([
          QUERY_NAME.CATEGORY_STATUS_USER_SETTINGS,
        ]);
        if (options?.showSuccessNotification ?? true) {
          notification.success({
            message: WORKER_SETTING_MESSAGES.RESET_SUCCESS,
          });
        }
      },
    },
  );
};
